import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { Button, Steps, Typography, Divider } from 'antd';
import { CheckOutlined, HeartFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import LPATestService from '../../services/LPATestService';
import { colors, calculateIsMinor, stepTitles } from './constants';
import { validationSchemas } from './validationSchemas';
import {
  WizardContainer, WizardHeader, MainTitle, StepContainer,
  NavigationButtons, StyledSteps, StyledButton, FormProgress,
  GlobalSelectStyles
} from './styledComponents';
import {
  StepTestSelection, StepPersonalInfo, StepDemographics,
  StepHealthHistory, StepConsents, StepResearchOptIns
} from './steps';

const { Step } = Steps;
const { Title, Paragraph } = Typography;

const LPATestRequestFormWizard = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: Test Selection
    labChoice: undefined,
    requestDate: moment().format('MM/DD/YYYY'),

    // Step 2: Personal & Contact Info
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    emailAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: undefined,
    zipCode: '',
    
    // Step 3: Demographics
    birthGender: '',
    raceEthnicity: undefined,
    householdIncome: '',
    insuranceStatus: undefined,
    
    // Step 4: Health History & Referral
    priorConditions: [],
    referredToCardiologist: '',
    // Step 5: Consents & Waivers
    procedureRisksAcknowledgement: false,
    emergencyAuthorization: false,
    communicationConsent: false,
    minorConsent: false,
    parentGuardianName: '',
    
    // Step 6: Research & Opt-Ins
    dataSharingPermission: '',
    heartGuardChampionOptIn: '',
    includeSpouseOrMinor: false,
    spouseFirstName: '',
    spouseLastName: '',
    spouseEmail: '',
    spouseRelationship: undefined,
    spouseDateOfBirth: '',
    spouseTestingCode: ''
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const nextStep = async (values, { setErrors, setTouched }) => {
    try {
      // For Step 5 (Consents & Waivers), handle special validation
      if (currentStep === 4) {
        const errors = {};
        const touched = {};
        
        if (!values.procedureRisksAcknowledgement) {
          errors.procedureRisksAcknowledgement = 'You must acknowledge the BHA Waiver & Consent Form';
          touched.procedureRisksAcknowledgement = true;
        }
        
        const combinedData = { ...formData, ...values };
        const isMinor = calculateIsMinor(combinedData.dateOfBirth);
        
        if (isMinor && values.minorConsent && !values.parentGuardianName?.trim()) {
          errors.parentGuardianName = 'Parent/Guardian name is required when minor consent is checked';
          touched.parentGuardianName = true;
        }
        
        if (Object.keys(errors).length > 0) {
          setErrors(errors);
          setTouched(touched);
          return;
        }
      } else {
        await validationSchemas[currentStep].validate(values, { abortEarly: false });
      }
      
      setFormData(values);
      setCurrentStep(currentStep + 1);
    } catch (validationError) {
      const errors = {};
      const touched = {};
      
      if (validationError.inner) {
        validationError.inner.forEach(error => {
          errors[error.path] = error.message;
          touched[error.path] = true;
        });
      }
      
      setErrors(errors);
      setTouched(touched);
    }
  };

  const prevStep = (values) => {
    setFormData(values);
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (values, { setErrors, setTouched }) => {
    try {
      setIsSubmitting(true);
      
      await validationSchemas[currentStep].validate(values, { abortEarly: false });
      
      const finalValues = { ...formData, ...values };
      setFormData(finalValues);
      
      const response = await LPATestService.submitLPATestRequest(finalValues);
      
      console.log('LPA test request submitted successfully:', response);
      setSubmitSuccess(true);
    } catch (error) {
      if (error.inner) {
        const errors = {};
        const touched = {};
        
        error.inner.forEach(err => {
          errors[err.path] = err.message;
          touched[err.path] = true;
        });
        
        setErrors(errors);
        setTouched(touched);
      } else {
        console.error('Error submitting LPA test request:', error);
        setSubmitError('There was an error submitting your LPA test request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Array of form step components
  const formSteps = [
    (props) => <StepTestSelection {...props} />,
    (props) => <StepPersonalInfo {...props} />,
    (props) => <StepDemographics {...props} />,
    (props) => <StepHealthHistory {...props} />,
    (props) => <StepConsents {...props} />,
    (props) => <StepResearchOptIns {...props} />
  ];

  if (submitSuccess) {
    return (
      <WizardContainer>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <HeartFilled style={{ fontSize: '48px', color: colors.primary, marginBottom: '24px' }} />
          <Title level={3} style={{ color: colors.primary, marginBottom: '16px' }}>Request Submitted!</Title>
          <Paragraph style={{ fontSize: '16px', color: colors.text, marginBottom: '24px' }}>
            Your LP(a) test request has been successfully submitted. You will receive a confirmation email shortly with instructions on how to proceed with your lab appointment.
          </Paragraph>
          <Button 
            type="primary" 
            size="large"
            onClick={() => {
              setSubmitSuccess(false);
              setCurrentStep(0);
              setFormData({
                labChoice: '',
                requestDate: moment().format('MM/DD/YYYY'),
                firstName: '',
                lastName: '',
                dateOfBirth: '',
                phoneNumber: '',
                emailAddress: '',
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                zipCode: '',
                birthGender: '',
                raceEthnicity: '',
                householdIncome: '',
                insuranceStatus: '',
                priorConditions: [],
                referredToCardiologist: '',
                procedureRisksAcknowledgement: false,
                emergencyAuthorization: false,
                communicationConsent: false,
                minorConsent: false,
                parentGuardianName: '',
                dataSharingPermission: '',
                heartGuardChampionOptIn: '',
                includeSpouseOrMinor: false,
                spouseFirstName: '',
                spouseLastName: '',
                spouseEmail: '',
                spouseRelationship: '',
                spouseDateOfBirth: '',
                spouseTestingCode: ''
              });
            }}
            style={{ backgroundColor: colors.primary, borderColor: colors.primary }}
          >
            Submit Another Request
          </Button>
        </div>
      </WizardContainer>
    );
  }

  if (submitError) {
    return (
      <WizardContainer>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Title level={3} style={{ color: colors.error, marginBottom: '16px' }}>Submission Error</Title>
          <Paragraph style={{ fontSize: '16px', color: colors.text, marginBottom: '24px' }}>
            {submitError}
          </Paragraph>
          <Button 
            type="primary" 
            onClick={() => setSubmitError('')}
            style={{ 
              height: '48px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none',
              backgroundColor: colors.primary, 
              borderColor: colors.primary, 
              marginRight: '12px' 
            }}
          >
            Try Again
          </Button>
          <Button 
            type="default" 
            onClick={() => {
              setSubmitError('');
              setCurrentStep(0);
            }}
            style={{ 
              height: '48px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'none'
            }}
          >
            Start Over
          </Button>
        </div>
      </WizardContainer>
    );
  }

  return (
    <GlobalSelectStyles>
      <WizardContainer>
        <WizardHeader>
          <MainTitle level={2}>
            Lipoprotein(a) Test Request
          </MainTitle>
          <Divider style={{ 
            margin: '16px 0 0 0', 
            borderColor: '#d9d9d9',
            borderWidth: '1px',
            borderStyle: 'solid'
          }} />
        </WizardHeader>

        <FormProgress>
          <span className="steps">Step {currentStep + 1} of {formSteps.length}: {stepTitles[currentStep]}</span>
        </FormProgress>
        
        <StyledSteps current={currentStep} size="small">
          {stepTitles.map((title, index) => (
            <Step key={index} title={title} />
          ))}
        </StyledSteps>

        <StepContainer>
          <Formik
            initialValues={formData}
            validationSchema={validationSchemas[currentStep]}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            onSubmit={(values, actions) => {
              if (currentStep === formSteps.length - 1) {
                handleSubmit(values, actions);
              } else {
                nextStep(values, actions);
              }
            }}
          >
            {(formikProps) => (
              <Form>
                {formSteps[currentStep](formikProps)}
                <NavigationButtons isLastStep={currentStep === formSteps.length - 1}>
                  {currentStep > 0 && (
                    <StyledButton 
                      type="default"
                      onClick={() => prevStep(formikProps.values)}
                      disabled={isSubmitting}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                      Back
                    </StyledButton>
                  )}
                  
                  {currentStep === 0 && <div />}
                  
                  <StyledButton
                    type="primary"
                    htmlType="submit"
                    size={currentStep === formSteps.length - 1 ? "large" : "middle"}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {currentStep === formSteps.length - 1 ? (
                      <>
                        {isSubmitting ? 'Submitting...' : 'Submit'} {!isSubmitting && <CheckOutlined />}
                      </>
                    ) : (
                      <>
                        Next <FontAwesomeIcon icon={faArrowRight} />
                      </>
                    )}
                  </StyledButton>
                </NavigationButtons>
              </Form>
            )}
          </Formik>
        </StepContainer>
      </WizardContainer>
    </GlobalSelectStyles>
  );
};

export default LPATestRequestFormWizard;
export { formatPhoneNumber } from './constants';
